import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import { useAdminUserList } from '@/hooks/useAdminUsers'
import type { AdminUser } from '@/lib/api/admin-users'
import { DataTable } from '@/components/shared/DataTable'
import { DataTableColumnHeader } from '@/components/shared/DataTableColumnHeader'
import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'
import { ExternalLink } from 'lucide-react'

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-red-100 text-red-800',
  canceled: 'bg-yellow-100 text-yellow-800',
  trialing: 'bg-blue-100 text-blue-800',
  lifetime: 'bg-purple-100 text-purple-800',
}

const columns: ColumnDef<AdminUser, unknown>[] = [
  {
    accessorKey: 'display_name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => (
      <span className="font-medium">{row.original.display_name || row.original.email}</span>
    ),
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ getValue }) => (
      <span className="text-sm text-muted-foreground">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'subscription_tier',
    header: 'Tier',
    cell: ({ getValue }) => {
      const tier = getValue() as string
      return (
        // Rank, not equality: `tier === 'pro'` would render the tier ABOVE Pro
        // with the lesser variant.
        <Badge variant={tier === 'pro' || tier === 'business' ? 'default' : 'secondary'}>
          {tier}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'subscription_status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as string
      return (
        <Badge variant="secondary" className={statusColors[status]}>
          {status}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'billing_cycle',
    header: 'Billing',
    cell: ({ getValue }) => (
      <span className="text-sm">{(getValue() as string) ?? '—'}</span>
    ),
  },
  {
    accessorKey: 'stripe_customer_id',
    header: 'Stripe',
    cell: ({ getValue }) => {
      const id = getValue() as string | null
      if (!id) return <span className="text-sm text-muted-foreground">—</span>
      return (
        <a
          href={`https://dashboard.stripe.com/customers/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      )
    },
  },
  {
    accessorKey: 'last_sign_in_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Active" />,
    sortingFn: (a, b) => {
      const av = a.original.last_sign_in_at ? new Date(a.original.last_sign_in_at).getTime() : 0
      const bv = b.original.last_sign_in_at ? new Date(b.original.last_sign_in_at).getTime() : 0
      return av - bv
    },
    cell: ({ getValue }) => {
      const date = getValue() as string | null
      if (!date) return <span className="text-sm text-muted-foreground">Never</span>
      return (
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(date), { addSuffix: true })}
        </span>
      )
    },
  },
]

export function SubscriptionListPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  // Load the full list and paginate + sort client-side — server-side
  // pagination defeats client-side sorting otherwise.
  const { data, isLoading } = useAdminUserList({
    pageSize: 100,
    tierFilter: ['pilot', 'pro', 'business'],
  })

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        description="Paid subscribers and their billing status"
      />

      <DataTable
        columns={columns}
        data={data?.users ?? []}
        isLoading={isLoading}
        onRowClick={(user) => navigate(`/users/${user.id}`)}
        emptyMessage="No paid subscribers yet."
        pagination={{ pageIndex: page - 1, pageSize: 20 }}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function'
            ? updater({ pageIndex: page - 1, pageSize: 20 })
            : updater
          setPage(next.pageIndex + 1)
        }}
      />
    </div>
  )
}
